import { useMemo } from 'react';

import { useGetCapabilities } from '@/hooks/api/useGetCapabilities';
import { useStatisticsBatch } from '@/hooks/useStatisticsBatch';
import { useVulnerabilityStatusMode } from '@/hooks/useVulnerabilityStatusMode';
import { useAuth } from '@/state/auth';
import { RiskPriority, RiskSeverity, SeverityDef } from '@/types';
import type { DropdownOption } from '@/types/dropdown.types';
import { getAvailableStatusOptions } from '@/utils/availableStatuses.util';
import { getShortName } from '@/utils/integrationDisplay.util';
import { transformSourceFilterOptions } from '@/utils/originDisplay.util';
import { capitalizeFirst } from '@/utils/text.util';

// Define GroupOption locally to avoid cross-imports from component files
type GroupOption = DropdownOption & {
  isGroup?: boolean;
  items?: DropdownOption[];
};

// Add a type declaration for window.CHARIOT_USER

declare global {
  interface Window {
    CHARIOT_USER?: { isPraetorianUser?: boolean };
  }
}

// Export the vulnerability field names for use in other components
export const VULNERABILITY_FIELD_NAMES = {
  name: 'name',
  severity: 'severity',
  priority: 'priority',
  status: 'status',
  origin: 'origin',
  tags: 'tags',
  attackSurface: 'attackSurface',
  cve: 'cve',
  cvss: 'cvss',
  epss: 'epss',
  kev: 'kev',
  exploit: 'exploit',
} as const;

export type VulnerabilityFieldName =
  (typeof VULNERABILITY_FIELD_NAMES)[keyof typeof VULNERABILITY_FIELD_NAMES];

/**
 * Optimization: Add sourceOnly mode to skip unnecessary statistics queries in drawer context
 * Performance: Reduces 8 statistics queries to 1 when only source data is needed
 *
 * @param hookOptions.enabled - Enable/disable query execution
 * @param hookOptions.sourceOnly - When true, only fetch source statistics (drawer mode)
 */
export const useGetVulnerabilityFilters = (hookOptions?: {
  enabled?: boolean;
  originOnly?: boolean;
}) => {
  const { enabled = true, originOnly: sourceOnly = false } = hookOptions || {};

  // Get user type to determine available statuses
  const { isPraetorianUser } = useAuth();

  // Get vulnerability status mode for mode-aware labels
  const { mode } = useVulnerabilityStatusMode();

  // Fetch capabilities to determine integrations vs Chariot capabilities
  const {
    integrations,
    chariotCapabilities,
    status: capabilitiesStatus,
  } = useGetCapabilities();

  // Batch fetch all statistics in a single request (8 queries → 1 request)
  // Performance: 90-97% faster than individual queries
  //
  // Optimization #1: sourceOnly mode for drawer context
  // Drawer only needs source statistics for column display
  // Main view needs all 8 statistics for filter dropdowns
  const batchStatistics = useStatisticsBatch({
    statisticKeys: sourceOnly
      ? [
          'risk#source#now', // Drawer: Only sources (needed for column display)
        ]
      : [
          'risk#source#now',
          'risk#class#now',
          'risk#tags',
          'risk#attackSurface#now',
          'risk#cvss#now',
          'risk#epss#now',
          'risk#kev#now',
          'risk#exploit#now',
        ], // Main view: All 8 statistics for filter dropdowns
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Extract individual statistics from batch response (memoized to avoid dependency issues)
  const sourceStats = useMemo(
    () => batchStatistics.data?.['risk#source#now'] || [],
    [batchStatistics.data]
  );
  const classStats = useMemo(
    () => batchStatistics.data?.['risk#class#now'] || [],
    [batchStatistics.data]
  );
  const tagStats = useMemo(
    () => batchStatistics.data?.['risk#tags'] || [],
    [batchStatistics.data]
  );
  const attackSurfaceStats = useMemo(
    () => batchStatistics.data?.['risk#attackSurface#now'] || [],
    [batchStatistics.data]
  );
  const cvssStats = useMemo(
    () => batchStatistics.data?.['risk#cvss#now'] || [],
    [batchStatistics.data]
  );
  const epssStats = useMemo(
    () => batchStatistics.data?.['risk#epss#now'] || [],
    [batchStatistics.data]
  );
  const kevStats = useMemo(
    () => batchStatistics.data?.['risk#kev#now'] || [],
    [batchStatistics.data]
  );
  const exploitStats = useMemo(
    () => batchStatistics.data?.['risk#exploit#now'] || [],
    [batchStatistics.data]
  );

  // Map priority (severity) options: use static list to ensure all are always available
  const severity = useMemo(() => {
    // Use static severity options to ensure all are always available
    // Use RiskSeverity letter codes as values (not numeric priorities) for backend compatibility
    return Object.values(RiskSeverity)
      .map(severity => ({
        value: severity, // Use letter code (C, H, M, L, I, E) for backend query
        label: SeverityDef[severity],
        id: severity,
        priority: RiskPriority[severity], // Add priority for sorting
      }))
      .sort((a, b) => a.priority - b.priority) // Sort by priority (ascending)
      .map(({ priority: _priority, ...item }) => item); // Remove priority from final result
  }, []);

  // Map status options: filtered by user type with mode-aware labels
  // Praetorian users see all statuses including Triaged
  // Customer users see all except Triaged
  const status = useMemo(() => {
    return getAvailableStatusOptions(isPraetorianUser, mode);
  }, [isPraetorianUser, mode]);

  const source = useMemo(() => {
    if (!sourceStats || capabilitiesStatus === 'pending') {
      return [];
    }

    const presentSources = (sourceStats || [])
      .filter(stat => stat.value !== undefined && stat.value !== null)
      .map(stat => String(stat.value));

    const chariotSources: DropdownOption[] = [];
    const integrationSources: DropdownOption[] = [];

    // Add special Chariot sources that aren't capabilities
    chariotSources.push(
      { label: 'Manual', value: '@praetorian.com', id: '@praetorian.com' },
      { label: 'Provided', value: 'provided', id: 'provided' },
      { label: 'Courier', value: 'burp-courier', id: 'burp-courier' },
      { label: 'Other', value: 'adhoc', id: 'adhoc' }
    );

    // Add Chariot capabilities that are present in the source stats
    chariotCapabilities.forEach(capability => {
      const capabilityName = capability.name.toLowerCase();
      if (presentSources.map(s => s.toLowerCase()).includes(capabilityName)) {
        // Find the exact value from presentSources for this capability
        const exactValue =
          presentSources.find(s => s.toLowerCase() === capabilityName) ||
          capability.name;

        // Use frontend SHORT_NAME_OVERRIDES mapping utility (consistent with integrations)
        const displayLabel = getShortName(
          capability.title || capability.name,
          capability.name
        );

        chariotSources.push({
          label: displayLabel,
          value: exactValue,
          id: exactValue,
        });
      }
    });

    // Process integrations from the integrations array
    integrations.forEach(integration => {
      const integrationName = integration.name.toLowerCase();
      if (presentSources.map(s => s.toLowerCase()).includes(integrationName)) {
        // Find the exact value from presentSources for this integration
        const exactValue =
          presentSources.find(s => s.toLowerCase() === integrationName) ||
          integration.name;

        // Use frontend SHORT_NAME_OVERRIDES mapping utility
        const displayLabel = getShortName(
          integration.title || integration.name,
          integration.name
        );

        integrationSources.push({
          label: displayLabel,
          value: exactValue,
          id: exactValue,
        });
      }
    });

    chariotSources.sort((a, b) => a.label.localeCompare(b.label));
    integrationSources.sort((a, b) => a.label.localeCompare(b.label));

    const result: (DropdownOption | GroupOption)[] = [];

    if (chariotSources.length > 0) {
      result.push({
        label: 'Chariot',
        value: 'chariot',
        id: 'chariot',
        isGroup: true,
        items: chariotSources,
      });
    }

    result.push(...integrationSources);

    // Apply NSA/CISA consolidation transformation
    return transformSourceFilterOptions(result);
  }, [sourceStats, integrations, chariotCapabilities, capabilitiesStatus]);

  const tag = useMemo(() => {
    if (!tagStats) return [];
    return tagStats
      .filter(tag => tag.created === 'now')
      .map((statistic, index) => ({
        value: statistic.value,
        label: statistic.value,
        id: `${statistic.name}-${index}`,
        key: `${statistic.name}${statistic.value}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [tagStats]);

  // Map attackSurface options: merge surface and class values, deduplicate
  const attackSurface = useMemo(() => {
    if (!attackSurfaceStats && !classStats) return [];

    const combinedValues = new Set<string>();

    (attackSurfaceStats || []).forEach(stat => {
      if (stat.value !== null && stat.value !== undefined) {
        combinedValues.add(String(stat.value).toLowerCase());
      }
    });

    (classStats || []).forEach(stat => {
      if (stat.value !== null && stat.value !== undefined) {
        combinedValues.add(String(stat.value).toLowerCase());
      }
    });

    const groups = {
      cloud: { label: 'Cloud', members: ['amazon', 'gcp', 'azure', 'cloud'] },
      repository: {
        label: 'Repository',
        members: ['github', 'gitlab', 'bitbucket', 'repository'],
      },
    };

    const result: DropdownOption[] = [];
    const remainingValues = new Set<string>(combinedValues);
    const addedGroups = new Set<string>();

    // Process groups and add them to the result if any of their members are present
    for (const [groupKey, groupInfo] of Object.entries(groups)) {
      const hasMember = groupInfo.members.some(member =>
        remainingValues.has(member)
      );

      if (hasMember && !addedGroups.has(groupKey)) {
        result.push({ value: groupKey, label: groupInfo.label, id: groupKey });
        addedGroups.add(groupKey);
        // Remove all group members from the set of remaining values
        groupInfo.members.forEach(member => remainingValues.delete(member));
      }
    }

    // Process all other values that were not part of any group
    remainingValues.forEach(value => {
      if (value) {
        // Ensure value is not an empty string
        // Now we check against actual integrations from capabilities endpoint
        const isIntegration = integrations.some(
          integration => integration.name.toLowerCase() === value.toLowerCase()
        );

        if (!isIntegration) {
          result.push({
            value: value,
            label: capitalizeFirst(value),
            id: value,
          });
        }
      }
    });

    return result.sort((a, b) => a.label.localeCompare(b.label));
  }, [attackSurfaceStats, classStats, integrations]);

  // Map cvss options
  const cvss = useMemo(() => {
    if (!cvssStats) return [];
    return cvssStats
      .filter(stat => stat.value !== undefined && stat.value !== null)
      .map(stat => ({
        value: String(stat.value),
        label: String(stat.value),
      }));
  }, [cvssStats]);

  // Map epss options
  const epss = useMemo(() => {
    if (!epssStats) return [];
    return epssStats
      .filter(stat => stat.value !== undefined && stat.value !== null)
      .map(stat => ({
        value: String(stat.value),
        label: String(stat.value),
      }));
  }, [epssStats]);

  // Map kev options
  const kev = useMemo(() => {
    if (!kevStats) return [];
    return kevStats
      .filter(stat => stat.value !== undefined && stat.value !== null)
      .map(stat => {
        const strValue = String(stat.value).toLowerCase();
        return {
          value: strValue,
          label: strValue === 'true' ? 'True' : 'False',
          id: strValue,
        };
      });
  }, [kevStats]);

  // Map exploit options
  const exploit = useMemo(() => {
    if (!exploitStats) return [];
    return exploitStats
      .filter(stat => stat.value !== undefined && stat.value !== null)
      .map(stat => {
        const strValue = String(stat.value).toLowerCase();
        return {
          value: strValue,
          label: strValue === 'true' ? 'True' : 'False',
          id: strValue,
        };
      });
  }, [exploitStats]);

  // Compose the filter options object
  const options = useMemo(
    () => ({
      severityFilters: severity,
      statusFilters: status,
      sourceFilters: source,
      tagFilters: tag,
      attackSurfaceFilters: attackSurface,
      cvss,
      epss,
      kevFilters: kev,
      exploitFilters: exploit,
    }),
    [severity, status, source, tag, attackSurface, cvss, epss, kev, exploit]
  );

  // Merge status - simplified with batch statistics
  // Note: priorityStatus and statusStatus removed since we use static options
  const mergeStatus = [batchStatistics.status, capabilitiesStatus].includes(
    'pending'
  )
    ? 'pending'
    : [batchStatistics.status, capabilitiesStatus].includes('error')
      ? 'error'
      : 'success';

  return { data: options, status: mergeStatus };
};
